import React, { useEffect, useRef } from "react";
import { getApplicationVariablesByApplicationId } from "../../api/controllers/applicationVariables";
import { getOrganizationVariablesByOrganizationId } from "../../api/controllers/organizationVariables";
import ApplicationVariablesContainer from "../../hooks/ApplicationVariablesContainer";
import CurrentApplicationContainer from "../../hooks/CurrentApplicationContainer";
import OrganizationVariablesContainer from "../../hooks/OrganizationVariablesContainer";
import {
    REFERENCE_VARIABLE_UPDATED_EVENT,
    ResolvedReferenceVariable
} from "../../utils/referenceVariables";

const ReferenceVariablesLoader: React.FC = ({ children }) => {
    const currentApplication = CurrentApplicationContainer.useContainer();
    const applicationVariables = ApplicationVariablesContainer.useContainer();
    const organizationVariables = OrganizationVariablesContainer.useContainer();
    const applicationRequestGeneration = useRef(0);
    const organizationRequestGeneration = useRef(0);

    useEffect(() => {
        const generation = ++applicationRequestGeneration.current;
        const applicationId = currentApplication.currentApplicationId;
        applicationVariables.setCurrentAppId(applicationId);

        if (!applicationId) {
            applicationVariables.setApplicationVariables([]);
            return;
        }

        applicationVariables.setApplicationVariables([]);

        void getApplicationVariablesByApplicationId(applicationId).then(result => {
            if (generation !== applicationRequestGeneration.current) {
                return;
            }

            applicationVariables.setApplicationVariables(
                result.resultType === "Ok" ? result.data || [] : []
            );
        }).catch(() => {
            if (generation === applicationRequestGeneration.current) {
                applicationVariables.setApplicationVariables([]);
            }
        });
    }, [currentApplication.currentApplicationId]);

    useEffect(() => {
        const generation = ++organizationRequestGeneration.current;
        const organizationId = currentApplication.currentOrganizationId;
        organizationVariables.setCurrentOrgId(organizationId);

        if (!organizationId) {
            organizationVariables.setOrganizationVariables([]);
            return;
        }

        organizationVariables.setOrganizationVariables([]);

        void getOrganizationVariablesByOrganizationId(organizationId).then(result => {
            if (generation !== organizationRequestGeneration.current) {
                return;
            }

            organizationVariables.setOrganizationVariables(
                result.resultType === "Ok" ? result.data || [] : []
            );
        }).catch(() => {
            if (generation === organizationRequestGeneration.current) {
                organizationVariables.setOrganizationVariables([]);
            }
        });
    }, [currentApplication.currentOrganizationId]);

    useEffect(() => {
        const handleVariableUpdated = (event: Event) => {
            const variable = (event as CustomEvent<ResolvedReferenceVariable>).detail;

            if (
                variable.scope === "Application"
                && variable.applicationId === currentApplication.currentApplicationId
            ) {
                applicationVariables.setApplicationVariables(currentVariables => (
                    currentVariables.map(current => (
                        current.id === variable.id
                            ? { ...current, value: variable.value }
                            : current
                    ))
                ));
            }

            if (
                variable.scope === "Organization"
                && variable.organizationId === currentApplication.currentOrganizationId
            ) {
                organizationVariables.setOrganizationVariables(currentVariables => (
                    currentVariables.map(current => (
                        current.id === variable.id
                            ? { ...current, value: variable.value }
                            : current
                    ))
                ));
            }
        };

        window.addEventListener(REFERENCE_VARIABLE_UPDATED_EVENT, handleVariableUpdated);
        return () => window.removeEventListener(REFERENCE_VARIABLE_UPDATED_EVENT, handleVariableUpdated);
    }, [
        currentApplication.currentApplicationId,
        currentApplication.currentOrganizationId
    ]);

    return React.createElement(React.Fragment, null, children);
};

export default ReferenceVariablesLoader;
